import { type PayloadAction, createSelector, createSlice, current } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import type { RootState } from ".";
import { type Plot, type PlotSeriesType, type PlotThreshold, isWeatherPlotSeries } from "./client-models";

import { fetchTab } from "@/api/hooks/tab";
import { createPlot } from "@/api/hooks/tools/plot";
import { HighchartsYAxis } from "@/overlay/components/CommonParts/PlotHighchart/utils/chart-options/axis-default";
import type { ModifiableYAxisOptions } from "@/overlay/components/LayerStackWindow/PlotYaxisOptions";
import { setAllToolLocation } from "@/reducer/TabsState";
import { hasCalibratedAvailable } from "@/utility/calibratedAvailabilityMap";
import Logger from "@mm/logging";
import type { ElementStyle, Location, LocationInfo } from "@mm/metx-workbench.meteomatics.com";
import type { YAxisOptions } from "highcharts";
import { DateTime, Duration } from "luxon";

const logger = Logger.fromFilename(__filename);

// Highcharts Y Axis provides a complete list of 8 objects for the Y axis. If any objects are missing, it automatically fills them in.
// See our HighchartsYAxis
function fillYAxisToFullLength(arr: YAxisOptions[]): YAxisOptions[] {
  if (arr.length < HighchartsYAxis.length) {
    const diff = HighchartsYAxis.length - arr.length;
    return arr.concat(HighchartsYAxis.slice(0, diff));
  }
  return arr;
}

export type PlotsState = {
  [id: number]: Plot;
};

const initialState: PlotsState = {};

const plotsSlice = createSlice({
  name: "plots",
  initialState: initialState as PlotsState,
  reducers: {
    addPlot(state, action: PayloadAction<Plot>) {
      state[action.payload.id] = action.payload;
    },
    discardPlot(state, action: PayloadAction<{ id: number }>) {
      const { id } = action.payload;
      const tool = state[id];

      if (!tool) {
        logger.error("Could not find tool with id", id);
        return;
      }
      delete state[id];
    },
    pastePlot(state, action: PayloadAction<{ id: number; plot: Partial<Plot> }>) {
      const { id, plot } = action.payload;
      const tool = state[id];

      if (!tool) {
        logger.error("Could not find tool with id", id);
        return;
      }
      state[id] = { ...tool, ...plot };
    },
    removePlotSerie(state, action: PayloadAction<{ serieIndex: PlotSeriesType["index"]; plotId: number }>) {
      const { serieIndex, plotId } = action.payload;
      const tool = state[plotId];

      if (!tool) {
        logger.error("Could not find tool with id", plotId);
        return;
      }
      const yAxisIndex = tool.plotSeries[serieIndex].series_y_axis;

      tool.plotSeries = tool.plotSeries
        .filter((plotSerie) => plotSerie.index !== serieIndex)
        .sort((plotSerie) => plotSerie.index)
        .map((plotSerie, index) => ({
          ...plotSerie,
          index,
        }));

      // remove visible yAxis if no series uses the current yAxisIndex
      const isYAxisStillUsed = tool.plotSeries.some((series) => series.series_y_axis === yAxisIndex);
      if (!isYAxisStillUsed && tool.plotYAxis[yAxisIndex]) {
        tool.plotYAxis[yAxisIndex].visible = false;
      }
    },
    addPlotSerie(state, action: PayloadAction<{ serie: PlotSeriesType; plotId: number }>) {
      const { serie, plotId } = action.payload;
      const tool = state[plotId];

      if (!tool) {
        logger.error("Could not find tool with id", plotId);
        return;
      }
      tool.plotSeries = [...tool.plotSeries, serie];
    },
    setPlotProps(state, action: PayloadAction<{ id: number; props: Partial<Plot> }>) {
      const { id, props } = action.payload;
      const plot = state[id];

      if (!plot) {
        logger.error("Could not find plot with id", id);
        return;
      }

      Object.assign(plot, props);
    },
    setPlotSerieProps(
      state,
      action: PayloadAction<{ id: number; plotSerieIdx: number; props: Partial<PlotSeriesType> }>,
    ) {
      const { id, plotSerieIdx, props } = action.payload;
      const plot = state[id];

      if (!plot) {
        logger.error("Could not find plot with id", id);
        return;
      }

      if (!plot.plotSeries[plotSerieIdx]) {
        logger.error("Could not find plotserie from Plot", plot.plotSeries);
        return;
      }

      const normalizedProps = { ...props };
      if (typeof normalizedProps.label === "string") {
        normalizedProps.label = normalizedProps.label.trim() || null;
      }

      const series = plot.plotSeries[plotSerieIdx];
      if (
        isWeatherPlotSeries(series) &&
        "parameter_unit" in normalizedProps &&
        normalizedProps.parameter_unit &&
        series.calibrated
      ) {
        normalizedProps.calibrated = hasCalibratedAvailable(normalizedProps.parameter_unit);
      }

      Object.assign(series, normalizedProps);
    },
    setPlotTitleStyleProps(state, action: PayloadAction<{ id: number; props: ElementStyle }>) {
      const { id, props } = action.payload;

      const plot = state[id];
      if (!plot) {
        logger.error("Could not find plot with id", id);
        return;
      }

      plot.titleStyle = { ...plot.titleStyle, ...props };
    },

    setPlotTitle(state, action: PayloadAction<{ id: number; title: string }>) {
      const { id, title } = action.payload;

      const plot = state[id];
      if (!plot) {
        logger.error("Could not find plot with id", id);
        return;
      }

      plot.title = title;
    },
    setPlotLocation(state, action: PayloadAction<{ id: number; props: Location }>) {
      const { id, props } = action.payload;

      const plot = state[id];
      if (!plot) {
        logger.error("Could not find plot with id", id);
        return;
      }

      plot.location = props;
    },
    setPlotLocationInfo(state, action: PayloadAction<{ id: number; props: LocationInfo }>) {
      const { id, props } = action.payload;

      const plot = state[id];
      if (!plot) {
        logger.error("Could not find plot with id", id);
        return;
      }

      plot.location_info = props;
    },
    setPlotDateTimeRange(
      state,
      action: PayloadAction<{
        id: number;
        props: { datetimeRange: [string | null, string | null]; resolution: string | null; referenceTime: string };
      }>,
    ) {
      const {
        id,
        props: {
          datetimeRange: [from, to],
          resolution,
          referenceTime,
        },
      } = action.payload;

      const plot = state[id];

      if (!plot) {
        logger.error("Could not find plot with id", id);
        return;
      }
      plot.resolution = resolution;
      if (!plot.time_lock) {
        plot.datetime_from = null;
        plot.datetime_to = null;

        plot.datetime_from_offset = from ? DateTime.fromISO(from).diff(DateTime.fromISO(referenceTime)).toISO() : null;
        plot.datetime_to_offset = to ? DateTime.fromISO(to).diff(DateTime.fromISO(referenceTime)).toISO() : null;

        return;
      }

      plot.datetime_from = from;
      plot.datetime_to = to;
    },
    adjustAllPlotDateTimeRange(state, action: PayloadAction<{ durationMinutes?: number }>) {
      const { durationMinutes } = action.payload;
      for (const plotId in state) {
        const currentPlot = state[plotId];

        if (currentPlot.time_lock) {
          continue;
        }

        const { datetime_from, datetime_to } = currentPlot;
        if (datetime_from) {
          const newFrom = DateTime.fromISO(datetime_from).plus({ minute: durationMinutes });
          const newTo = datetime_to ? DateTime.fromISO(datetime_to).plus({ minute: durationMinutes }) : null;

          currentPlot.datetime_from = newFrom.toISO();
          currentPlot.datetime_to = newTo?.toISO() || null;
        }
      }
    },
    setPlotYaxisOption(
      state,
      action: PayloadAction<{ id: number; plotSerieYAxisIdx: number; yAxis: ModifiableYAxisOptions }>,
    ) {
      const { id, plotSerieYAxisIdx, yAxis } = action.payload;

      const plot = state[id];
      if (!plot) {
        logger.error("Could not find plot with id", id);
        return;
      }

      if (!plot.plotYAxis[plotSerieYAxisIdx]) {
        logger.error("Could not find plotYAxis from Plot", plot.plotYAxis);
        return;
      }
      plot.plotYAxis[plotSerieYAxisIdx] = { ...plot.plotYAxis[plotSerieYAxisIdx], ...yAxis };
    },
    resetUnusedYaxes(state, action: PayloadAction<{ plotId: number }>) {
      const { plotId } = action.payload;
      const tool = state[plotId];

      if (!tool) {
        logger.error("Could not find tool with id", plotId);
        return;
      }

      const usedYAxisIndices = new Set<number>(tool.plotSeries.map((series) => series.series_y_axis));
      for (let yAxisIndex = 0; yAxisIndex <= 6; yAxisIndex++) {
        if (!usedYAxisIndices.has(yAxisIndex) && tool.plotYAxis[yAxisIndex]) {
          tool.plotYAxis[yAxisIndex].visible = false;
        }
      }
    },
    setPlotLegendStyleProps(state, action: PayloadAction<{ id: number; props: ElementStyle }>) {
      const { id, props } = action.payload;

      const plot = state[id];
      if (!plot) {
        logger.error("Could not find plot with id", id);
        return;
      }

      plot.legendStyle = { ...plot.legendStyle, ...props };
    },
    setPlotSerieOrder(
      state,
      action: PayloadAction<{ id: number; plotSerieIdx: PlotSeriesType["index"]; moveUpBy: number }>,
    ) {
      const { id, plotSerieIdx, moveUpBy } = action.payload;

      const plot = state[id];

      if (!plot) {
        logger.error("Could not find plot with id", id);
        return;
      }

      if (!plot.plotSeries) {
        logger.error("Could not get plotSeries from plot: ", plot);
        return;
      }
      // Sort the layers so they are from the smaller z index to the bigger z index.
      const copyOfPlotSeries = [...plot.plotSeries];
      const sortedPlotSeries = copyOfPlotSeries.concat().sort((a, b) => (a.index < b.index ? -1 : 1));
      // Get the current position of the target layer and the index to move the target to.
      const fromIdx = sortedPlotSeries[plotSerieIdx] ? sortedPlotSeries[plotSerieIdx].index : -1;

      if (fromIdx === undefined) {
        // Use "=== undefined" otherwise index -1 and 1 evaluate to true/false.
        logger.error("Could not get PlotSeries from plot with idx", plotSerieIdx);
        return;
      }
      let toIdx = fromIdx - moveUpBy;

      if (toIdx >= sortedPlotSeries.length) {
        // If the toIdx is bigger than the legnth, set it to the last idx
        toIdx = sortedPlotSeries.length - 1;
      }
      if (toIdx < 0) {
        // If toIdx is smaller than 0, just set it to the first index
        toIdx = 0;
      }
      // Move the target layer to the toIdx
      const targetLayer = sortedPlotSeries.splice(fromIdx, 1)[0];
      sortedPlotSeries.splice(toIdx, 0, targetLayer);
      // Re-asign the z-index to each layer according to the order in the array
      plot.plotSeries = sortedPlotSeries.map((plotSerie, i) => {
        plotSerie.index = i; //Z-index starts from 1.
        return plotSerie as PlotSeriesType;
      });
    },
    setPlotTimeSettings(
      state,
      action: PayloadAction<{
        id: number;
        timeLock: boolean;
        from: string;
        to: string;
        resolution: string;
        referenceTime: string;
      }>,
    ) {
      const { id, timeLock, from, to, resolution, referenceTime } = action.payload;

      const plot = state[id];
      if (!plot) {
        logger.error("Could not find plot with id", id);
        return;
      }

      plot.time_lock = timeLock;

      if (!timeLock) {
        plot.datetime_from = null;
        plot.datetime_from_offset = from ? DateTime.fromISO(from).diff(DateTime.fromISO(referenceTime)).toISO() : null;

        plot.datetime_to = null;
        plot.datetime_to_offset = to ? DateTime.fromISO(to).diff(DateTime.fromISO(referenceTime)).toISO() : null;

        return;
      }

      plot.datetime_from = plot.datetime_from_offset
        ? DateTime.fromISO(referenceTime).plus(Duration.fromISO(plot.datetime_from_offset)).toISO()
        : DateTime.fromISO(from).toISO();
      plot.datetime_from_offset = null;

      plot.datetime_to = plot.datetime_to_offset
        ? DateTime.fromISO(referenceTime).plus(Duration.fromISO(plot.datetime_to_offset)).toISO()
        : DateTime.fromISO(to).toISO();
      plot.datetime_to_offset = null;

      plot.resolution = resolution;
    },
    addPlotSeriesThreshold(
      state,
      action: PayloadAction<{ threshold: PlotThreshold; plotId: number; plotSerieIdx: number }>,
    ) {
      const { threshold, plotId, plotSerieIdx } = action.payload;
      const plot = state[plotId];

      if (!plot) {
        logger.error("Could not find plot with id", plotId);
        return;
      }

      if (!plot.plotSeries[plotSerieIdx]) {
        logger.error("Could not find plotserie from Plot", plot.plotSeries);
        return;
      }
      const currentThresholds = plot.plotSeries[plotSerieIdx].thresholds ?? [];

      plot.plotSeries[plotSerieIdx].thresholds = [...currentThresholds, threshold];
    },
    setPlotSeriesThresholds(
      state,
      action: PayloadAction<{
        plotId: number;
        plotSerieIdx: number;
        thresholdIdx: number;
        props: Partial<PlotThreshold>;
      }>,
    ) {
      const { plotId, plotSerieIdx, thresholdIdx, props } = action.payload;

      const plot = state[plotId];
      if (!plot) {
        logger.error("Could not find plot with id", plotId);
        return;
      }

      const plotSeries = plot.plotSeries[plotSerieIdx];
      if (!plotSeries) {
        logger.error("Could not find plotserie from Plot", plot.plotSeries);
        return;
      }

      const currentThresholds = plot.plotSeries[plotSerieIdx].thresholds ?? [];
      if (!currentThresholds[thresholdIdx]) {
        logger.error("Could not find threshold from Plot", currentThresholds);
        return;
      }

      currentThresholds[thresholdIdx] = { ...currentThresholds[thresholdIdx], ...props };
    },
    setThresholdsOrder(
      state,
      action: PayloadAction<{
        plotId: number;
        plotSerieIdx: number;
        thresholdIdx: number;
        moveUpBy: number;
      }>,
    ) {
      const { plotId, plotSerieIdx, thresholdIdx, moveUpBy } = action.payload;

      const plot = state[plotId];

      if (!plot) {
        logger.error("Could not find plot with id", plotId);
        return;
      }

      const plotSeries = plot.plotSeries[plotSerieIdx];
      if (!plotSeries) {
        logger.error("Could not find plotserie from Plot", plot.plotSeries);
        return;
      }

      const copyOfThresholds = [...(plotSeries.thresholds ?? [])];
      const sortedThresholds = copyOfThresholds.concat().sort((a, b) => (a.index < b.index ? -1 : 1));
      const fromIdx = sortedThresholds[thresholdIdx].index;

      let toIdx = fromIdx - moveUpBy;
      if (toIdx >= sortedThresholds.length) {
        toIdx = sortedThresholds.length - 1;
      }
      if (toIdx < 0) {
        // If toIdx is smaller than 0, just set it to the first index
        toIdx = 0;
      }
      // Move the target to the toIdx
      const targetThreshold = sortedThresholds.splice(fromIdx, 1)[0];
      sortedThresholds.splice(toIdx, 0, targetThreshold);
      // Re-asign the z-index to each treshold according to the order in the array
      plotSeries.thresholds = sortedThresholds.map((threshold, i) => {
        threshold.index = i;
        return threshold as PlotThreshold;
      });
    },
    removePlotSeriesThreshold(
      state,
      action: PayloadAction<{
        plotId: number;
        plotSerieIdx: number;
        thresholdIdx: number;
      }>,
    ) {
      const { plotId, plotSerieIdx, thresholdIdx } = action.payload;
      const plot = state[plotId];

      if (!plot) {
        logger.error("Could not find tool with id", plotId);
        return;
      }
      const plotSeries = plot.plotSeries[plotSerieIdx];
      if (!plotSeries) {
        logger.error("Could not find plotserie from Plot", plot.plotSeries);
        return;
      }

      const currentThresholds = plot.plotSeries[plotSerieIdx].thresholds ?? [];
      if (!currentThresholds[thresholdIdx]) {
        logger.error("Could not find threshold from Plot", currentThresholds);
        return;
      }

      plot.plotSeries[plotSerieIdx].thresholds = currentThresholds
        .filter((threshold) => threshold.index !== thresholdIdx)
        .sort((threshold) => threshold.index)
        .map((threshold, index) => ({
          ...threshold,
          index,
        }));
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchTab.fulfilled, (state, action) => {
      const tab = action.payload;
      for (const key in state) {
        delete state[+key];
      }
      for (const plot of tab.plots) {
        // We have a backend model GenericPlot with some JSON fields.
        // And a frontend model Plot. Here, we cast it to Plot as we always use Plot in frontend.
        plot.plotYAxis = fillYAxisToFullLength(plot.plotYAxis);
        state[plot.id] = plot as Plot;
      }
    });
    builder.addCase(createPlot.fulfilled, (state, action) => {
      const plot = action.payload.tool;
      state[plot.id] = plot;
    });
    builder.addCase(setAllToolLocation, (state, action) => {
      const { lat, lon, label } = action.payload.location;
      for (const key in state) {
        const plot = state[+key];
        if (!plot) {
          continue;
        }
        plot.location = { lat, lng: lon };
        plot.location_info = { coordinates: { lat, lon }, label };
      }
    });
  },
});

const selectPlots = (state: RootState) => state.tabGroup.present.plots;

const makeSelectPlotsByTabId = createSelector(
  [selectPlots, (state: RootState, tabId: number) => tabId],
  (plots, tabId) => Object.values(plots).filter((plot) => plot.id_tab === tabId),
);

export function usePlots(tabId: number) {
  return useSelector((state: RootState) => makeSelectPlotsByTabId(state, tabId));
}

const makeSelectPlotById = createSelector(
  [selectPlots, (state: RootState, id: number) => id],
  (plots, id) => plots[id],
);

export function usePlot(id: number) {
  return useSelector((state: RootState) => makeSelectPlotById(state, id));
}

export const {
  addPlot,
  pastePlot,
  setPlotProps,
  setPlotSerieProps,
  removePlotSerie,
  addPlotSerie,
  setPlotTitleStyleProps,
  setPlotTitle,
  setPlotDateTimeRange,
  adjustAllPlotDateTimeRange,
  setPlotLegendStyleProps,
  setPlotYaxisOption,
  resetUnusedYaxes,
  setPlotSerieOrder,
  setPlotLocation,
  setPlotLocationInfo,
  discardPlot,
  setPlotTimeSettings,
  addPlotSeriesThreshold,
  setPlotSeriesThresholds,
  setThresholdsOrder,
  removePlotSeriesThreshold,
} = plotsSlice.actions;

export default plotsSlice.reducer;
